import { createLogger } from '@/utils/logger';
import type { QualityTier } from '@/types';

const log = createLogger('caps');

export type RenderBackend = 'webgpu' | 'webgl';

export interface GPUCapabilities {
  /** True when a `WebGPUDevice` could actually be acquired (not just `navigator.gpu`). */
  webgpu: boolean;
  /** True when a WebGL2 context is creatable. */
  webgl2: boolean;
  /** True when any WebGL context is creatable at all. */
  webgl: boolean;
  /** Which backend the app SHOULD use given the device + current engine support. */
  backend: RenderBackend;
  /** Hardware GPU detected (vs software/swiftshader fallback). */
  hardwareAccelerated: boolean;
  /** Best-effort adapter/renderer description for logging. */
  adapter: string;
  /** Whether the device looks like a low-power / mobile GPU (gates the bloom pass). */
  lowPower: boolean;
  /** A phone/tablet by user agent (2026-09-04). Even a MEDIUM-tier phone GPU (Adreno 7xx,
   *  Mali-G, Apple A-series) must not run the complex planar-reflection water — its mediump
   *  fragment path and the nested scene re-render are exactly what "just broke" there. Gates
   *  the water's simple mode regardless of tier. */
  mobile: boolean;
  /**
   * The quality tier this machine should START on (2026-08-18, user: "if someone's PC is good,
   * on that PC he will get full performance — you have to serve according to the device").
   *
   * Distinct from {@link lowPower}, which is a single coarse "is this a weak GPU" flag and was
   * doing double duty. It matched `/Intel/` against the adapter string, so EVERY Intel part —
   * including a perfectly capable Iris Xe or Arc — was treated the same as a decade-old UHD
   * chip. This is a real three-way classification instead, so a strong machine is never held
   * back and a genuinely weak one doesn't have to render three bad seconds before the FPS
   * monitor reacts.
   *
   * This is only the STARTING POINT. `PerformanceMonitor` still adapts in both directions from
   * here, so a misjudged device corrects itself within a few seconds either way.
   */
  tier: QualityTier;
  /** Reported system RAM in GB (Chrome's `navigator.deviceMemory`; undefined elsewhere). */
  memoryGb: number | undefined;
  /** Logical CPU cores (`navigator.hardwareConcurrency`). */
  cores: number | undefined;
}

/**
 * Detects the rendering capabilities of the user's device once, at boot.
 *
 * The engine currently RENDERS through WebGL (Three's `WebGLRenderer`), so
 * `backend` resolves to `'webgl'` today even when WebGPU is available — but we
 * probe `navigator.gpu` for real (request an adapter + device) and expose the
 * result so a future WebGPU/TSL render path can switch on `caps.webgpu` without
 * any new detection work. See the renderer seam in {@link selectBackend}.
 */
export class Capabilities {
  private static cached: GPUCapabilities | null = null;

  /** Run detection once; subsequent calls return the cached result. */
  static async detect(): Promise<GPUCapabilities> {
    if (this.cached) return this.cached;

    const webgpu = await this.probeWebGPU();
    const { webgl, webgl2, adapter, hardwareAccelerated } = this.probeWebGL();
    const lowPower = this.isLowPower(adapter);
    const nav = navigator as Navigator & { deviceMemory?: number };
    const memoryGb = nav.deviceMemory;
    const cores = navigator.hardwareConcurrency || undefined;

    const caps: GPUCapabilities = {
      webgpu: webgpu.ok,
      webgl2,
      webgl,
      hardwareAccelerated,
      adapter: webgpu.ok ? `${webgpu.adapter} (WebGPU) / ${adapter}` : adapter,
      lowPower,
      mobile: /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent),
      memoryGb,
      cores,
      tier: this.classifyTier(adapter, hardwareAccelerated, memoryGb, cores),
      backend: this.selectBackend(webgpu.ok),
    };

    this.cached = caps;
    log.info(
      `GPU: ${caps.adapter} — webgpu:${caps.webgpu} webgl2:${caps.webgl2} ` +
        `hwAccel:${caps.hardwareAccelerated} ram:${caps.memoryGb ?? '?'}GB cores:${caps.cores ?? '?'} ` +
        `→ tier:${caps.tier} backend:${caps.backend}`,
    );
    return caps;
  }

  /**
   * Pick the starting quality tier from the GPU, RAM and core count.
   *
   * Ordered most-certain-first. The adapter string is the strongest signal available in a
   * browser, but it is a free-form vendor string, so the patterns are deliberately broad and the
   * RAM/CPU caps at the end act as a backstop for the case the user actually hit: a laptop with
   * "less RAM and no GPU". `PerformanceMonitor` corrects any mistake within seconds, in either
   * direction, so being roughly right immediately beats being exactly right eventually.
   */
  private static classifyTier(
    adapter: string,
    hardwareAccelerated: boolean,
    memoryGb: number | undefined,
    cores: number | undefined,
  ): QualityTier {
    // Software rasteriser (SwiftShader / llvmpipe) — nothing else matters.
    if (!hardwareAccelerated) return 'low';

    const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    // Discrete desktop GPUs and Apple Silicon — these run the full scene comfortably.
    const strong = /RTX|GTX|GeForce|Radeon\s*(RX|Pro)|Quadro|Arc\s*A|Apple\s*M\d/i.test(adapter);
    // Old / genuinely weak integrated parts.
    const weak = /Intel.*(HD Graphics|UHD Graphics)|HD Graphics \d|Mali-[T4]|Adreno [1-5]\d\d|PowerVR/i.test(adapter);
    // Recent integrated — capable, but not a discrete card.
    const mid = /Iris|Intel.*\bXe\b|Vega|Radeon\s*Graphics|Mali-G|Adreno [6-9]\d\d/i.test(adapter);

    let tier: QualityTier;
    if (weak) tier = 'low';
    else if (strong && !mobile) tier = 'high';
    else if (mid || strong) tier = 'medium';
    else if (mobile) tier = 'low';
    else tier = 'medium'; // unknown desktop part — start in the middle and let FPS decide

    // RAM / CPU backstop. `deviceMemory` is capped at 8 by the spec and is Chrome-only, so a
    // missing value must NOT be read as "weak" — only act on a value we actually got.
    if ((memoryGb !== undefined && memoryGb <= 4) || (cores !== undefined && cores <= 2)) {
      tier = 'low';
    } else if (tier === 'high' && cores !== undefined && cores <= 4) {
      tier = 'medium';
    }
    return tier;
  }

  static get current(): GPUCapabilities | null {
    return this.cached;
  }

  /**
   * Which backend to render with. Today: always `'webgl'` (the engine's shaders are
   * GLSL `ShaderMaterial`s, which Three's WebGPURenderer can't run). When the TSL
   * render path lands, flip this to `webgpuAvailable ? 'webgpu' : 'webgl'`.
   */
  private static selectBackend(webgpuAvailable: boolean): RenderBackend {
    void webgpuAvailable;
    return 'webgl';
  }

  /** Actually try to acquire a WebGPU adapter + device (not just feature-detect). */
  private static async probeWebGPU(): Promise<{ ok: boolean; adapter: string }> {
    const gpu = (navigator as Navigator & { gpu?: GPU }).gpu;
    if (!gpu) return { ok: false, adapter: 'none' };
    try {
      const adapter = await gpu.requestAdapter({ powerPreference: 'high-performance' });
      if (!adapter) return { ok: false, adapter: 'no-adapter' };
      const device = await adapter.requestDevice();
      const ok = !!device;
      // `info` is the standardized adapter descriptor (vendor/architecture).
      const info = (adapter as GPUAdapter & { info?: GPUAdapterInfo }).info;
      const name = info ? [info.vendor, info.architecture].filter(Boolean).join(' ') : 'webgpu';
      device?.destroy?.();
      return { ok, adapter: name || 'webgpu' };
    } catch (error) {
      log.debug('WebGPU probe failed', error);
      return { ok: false, adapter: 'error' };
    }
  }

  /** Create throwaway WebGL contexts to read the unmasked renderer string. */
  private static probeWebGL(): {
    webgl: boolean;
    webgl2: boolean;
    adapter: string;
    hardwareAccelerated: boolean;
  } {
    const canvas = document.createElement('canvas');
    const gl2 = canvas.getContext('webgl2');
    const gl = (gl2 ?? canvas.getContext('webgl')) as WebGLRenderingContext | null;
    if (!gl) {
      return { webgl: false, webgl2: false, adapter: 'no-webgl', hardwareAccelerated: false };
    }

    let adapter = 'unknown';
    const ext = gl.getExtension('WEBGL_debug_renderer_info');
    if (ext) {
      adapter = String(gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) ?? 'unknown');
    }
    // SwiftShader / llvmpipe / "software" → no real GPU.
    const hardwareAccelerated = !/swiftshader|software|llvmpipe|basic render/i.test(adapter);

    const loseCtx = gl.getExtension('WEBGL_lose_context');
    loseCtx?.loseContext();

    return { webgl: true, webgl2: !!gl2, adapter, hardwareAccelerated };
  }

  /** Heuristic: integrated/mobile GPUs → start at lower cloud quality. */
  private static isLowPower(adapter: string): boolean {
    const mobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent);
    const integrated = /Apple|Mali|Adreno|PowerVR|Intel|UHD|Iris/i.test(adapter);
    return mobile || integrated;
  }
}
